import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  Circle, 
  Lock, 
  Clock, 
  AlertCircle,
  ChevronRight,
  Loader2,
  PartyPopper,
  Mail,
  User,
  Building2,
  FileText,
  Shield,
  Send
} from "lucide-react";

const stepIcons = {
  1: User,
  2: Mail,
  3: User,
  4: Building2,
  5: FileText,
  6: Shield,
};

const statusColors = {
  completed: "text-green-600 bg-green-100",
  current: "text-court-orange bg-court-orange/10",
  in_review: "text-blue-600 bg-blue-100",
  pending: "text-slate-400 bg-slate-100",
  locked: "text-slate-300 bg-slate-50",
};

const statusIcons = {
  completed: CheckCircle2,
  current: Circle,
  in_review: Clock,
  pending: Circle,
  locked: Lock,
};

export default function OnboardingProgress({ compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);

  const fetchData = () => {
    api.get("/provider/onboarding-status")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate remaining cooldown time
  const getRemainingCooldown = (lastSentAt) => {
    if (!lastSentAt) return 0;
    try {
      const lastSent = new Date(lastSentAt);
      const now = new Date();
      const minutesSinceSent = (now - lastSent) / (1000 * 60);
      const remaining = Math.ceil(30 - minutesSinceSent);
      return remaining > 0 ? remaining : 0;
    } catch {
      return 0;
    }
  };

  // Handle resend verification email
  const handleResendVerification = async (userEmail) => {
    if (!userEmail) return;
    
    setResending(true);
    try {
      const res = await api.post("/venue-onboarding/resend-verification", { email: userEmail });
      toast.success(res.data.message || `Verification email sent to ${userEmail}!`);
      // Refresh data to update cooldown timer
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to resend verification email.";
      toast.error(detail);
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border-2 border-slate-100 p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-court-orange" />
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  // Show congratulations for approved users in compact mode
  if (data.overall_status === "approved" && compact) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-lime-50 border-2 border-green-200 p-4 md:p-5" data-testid="onboarding-approved-banner">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <PartyPopper className="w-6 h-6 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-green-800">Venue Verified!</h3>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-heading uppercase tracking-wide">
                <CheckCircle2 className="w-3 h-3" /> Live
              </span>
            </div>
            <p className="text-sm text-green-700">Your venue is active and accepting bookings. Keep it up!</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = () => {
    switch (data.overall_status) {
      case "approved":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-heading uppercase tracking-wide">
            <CheckCircle2 className="w-3.5 h-3.5" /> Verified
          </span>
        );
      case "pending_approval":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-heading uppercase tracking-wide">
            <Clock className="w-3.5 h-3.5" /> Under Review
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-heading uppercase tracking-wide">
            <AlertCircle className="w-3.5 h-3.5" /> Needs Attention
          </span>
        );
      case "pending_email":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-heading uppercase tracking-wide">
            <Mail className="w-3.5 h-3.5" /> Email Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-heading uppercase tracking-wide">
            <Circle className="w-3.5 h-3.5" /> In Progress
          </span>
        );
    }
  };

  // Find the next action step
  const nextStep = data.steps.find(s => s.status === "current" || s.status === "pending");

  // Compact version for dashboard
  if (compact) {
    return (
      <div className="bg-gradient-to-r from-court-orange/5 to-lime-volt/5 border-2 border-court-orange/20 p-4 md:p-6" data-testid="onboarding-progress-compact">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Progress Circle */}
          <div className="flex items-center gap-4 flex-1">
            <div className="relative w-16 h-16 flex-shrink-0">
              <svg className="w-16 h-16 transform -rotate-90">
                <circle cx="32" cy="32" r="28" stroke="#e5e7eb" strokeWidth="4" fill="none" />
                <circle 
                  cx="32" cy="32" r="28" 
                  stroke="#FF6B35" 
                  strokeWidth="4" 
                  fill="none"
                  strokeDasharray={`${(data.progress_percentage / 100) * 176} 176`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-heading font-bold text-lg">{data.progress_percentage}%</span>
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wide">Onboarding Progress</h3>
                {getStatusBadge()}
              </div>
              <p className="text-sm text-muted-foreground truncate">{data.status_message}</p>
              <p className="text-xs text-slate-400 mt-1">{data.completed_steps} of {data.total_steps} steps completed</p>
            </div>
          </div>

          {/* Next Action Button - Regular navigation */}
          {nextStep?.action_url && (
            <Button 
              onClick={() => navigate(nextStep.action_url)}
              className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide text-xs shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
              data-testid="onboarding-next-action"
            >
              {nextStep.title}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          
          {/* Next Action Button - Resend Verification Email */}
          {nextStep?.action_type === "resend_verification" && !nextStep.completed && (
            <div className="flex flex-col items-end gap-1">
              {(() => {
                const cooldownRemaining = getRemainingCooldown(nextStep.last_verification_sent);
                const isOnCooldown = cooldownRemaining > 0;
                
                return (
                  <>
                    <Button 
                      onClick={() => handleResendVerification(nextStep.user_email)}
                      disabled={resending || isOnCooldown}
                      className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide text-xs shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                      data-testid="onboarding-resend-email"
                    >
                      {resending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Sending...
                        </>
                      ) : isOnCooldown ? (
                        <>
                          <Clock className="w-4 h-4 mr-1" />
                          Wait {cooldownRemaining}m
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Verify Email
                        </>
                      )}
                    </Button>
                    {nextStep.user_email && (
                      <p className="text-[10px] text-muted-foreground truncate max-w-[180px]" title={nextStep.user_email}>
                        Sends to: {nextStep.user_email}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full version
  return (
    <div className="bg-white border-2 border-slate-100 p-6" data-testid="onboarding-progress-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Venue Onboarding</h2>
            {getStatusBadge()}
          </div>
          <p className="text-muted-foreground text-sm">{data.status_message}</p>
        </div>
        
        {data.overall_status === "approved" && (
          <div className="flex items-center gap-2 text-green-600">
            <PartyPopper className="w-6 h-6" />
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="font-heading uppercase tracking-wide text-muted-foreground">Progress</span>
          <span className="font-heading font-bold">{data.progress_percentage}%</span>
        </div>
        <Progress value={data.progress_percentage} className="h-2" />
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {data.steps.map((step, index) => {
          const StepIcon = stepIcons[step.step] || Circle;
          const StatusIcon = statusIcons[step.status] || Circle;
          const isLast = index === data.steps.length - 1;
          
          return (
            <div key={step.step} className="relative" data-testid={`onboarding-step-${step.step}`}>
              {/* Connector line */}
              {!isLast && (
                <div className={`absolute left-5 top-10 w-0.5 h-8 ${step.completed ? 'bg-green-200' : 'bg-slate-200'}`} />
              )}
              
              <div className={`flex items-start gap-4 p-3 rounded-lg transition-colors ${
                step.status === "current" ? "bg-court-orange/5 border border-court-orange/20" : ""
              }`}>
                {/* Step Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${statusColors[step.status]}`}>
                  {step.completed ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : step.status === "locked" ? (
                    <Lock className="w-4 h-4" />
                  ) : step.status === "in_review" ? (
                    <Clock className="w-5 h-5" />
                  ) : (
                    <StepIcon className="w-5 h-5" />
                  )}
                </div>
                
                {/* Step Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className={`font-heading font-semibold text-sm ${
                      step.status === "locked" ? "text-slate-400" : "text-slate-900"
                    }`}>
                      {step.title}
                    </h4>
                    {step.completed && (
                      <span className="text-[10px] font-heading uppercase tracking-wide text-green-600">Done</span>
                    )}
                    {step.status === "in_review" && (
                      <span className="text-[10px] font-heading uppercase tracking-wide text-blue-600">Reviewing</span>
                    )}
                  </div>
                  <p className={`text-xs ${step.status === "locked" ? "text-slate-300" : "text-muted-foreground"}`}>
                    {step.description}
                  </p>
                </div>
                
                {/* Action Button */}
                {step.action_url && step.status !== "locked" && !step.completed && (
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(step.action_url)}
                    className="flex-shrink-0 rounded-none border-2 text-xs font-heading uppercase"
                    data-testid={`step-${step.step}-action`}
                  >
                    Start
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
                
                {/* Special handling for Verify Email step */}
                {step.action_type === "resend_verification" && !step.completed && (
                  <div className="flex-shrink-0">
                    {(() => {
                      const cooldownRemaining = getRemainingCooldown(step.last_verification_sent);
                      const isOnCooldown = cooldownRemaining > 0;
                      
                      return (
                        <div className="text-right">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => handleResendVerification(step.user_email)}
                            disabled={resending || isOnCooldown}
                            className="rounded-none border-2 text-xs font-heading uppercase"
                            data-testid={`step-${step.step}-resend`}
                          >
                            {resending ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Sending...
                              </>
                            ) : isOnCooldown ? (
                              <>
                                <Clock className="w-3 h-3 mr-1" />
                                Wait {cooldownRemaining}m
                              </>
                            ) : (
                              <>
                                <Send className="w-3 h-3 mr-1" />
                                Resend
                              </>
                            )}
                          </Button>
                          {step.user_email && (
                            <p className="text-[10px] text-muted-foreground mt-1 max-w-[140px] truncate" title={step.user_email}>
                              To: {step.user_email}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Venue Info (if exists) */}
      {data.primary_venue?.venue_id && (
        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-heading uppercase tracking-wide text-muted-foreground mb-1">Primary Venue</p>
              <p className="font-medium">{data.primary_venue.name}</p>
            </div>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/provider/venues`)}
              className="text-xs"
            >
              View Venues
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
